import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Popconfirm, Select, Skeleton, Radio, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import EventApplicationsModal from './eventApplicationModal';
const { Option } = Select;

const EventTable = ({ events, loading, openPopup, userRole, handleDeleteEvent, handleEditEvent }) => {
  const [filteredEvents, setFilteredEvents] = useState(events || []);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [eventType, setEventType] = useState('solo'); // State for radio button selection  
  const [isModalVisible, setIsModalVisible] = useState(false); // State for Modal visibility  
  const [currentEvent, setCurrentEvent] = useState(null); // State for the current event  

  // Function to get unique event names for the filters  
  const getUniqueEventNames = () => {
    return [...new Set((events || [])
      .filter(event => (eventType === 'solo' ? event.solo_bool : !event.solo_bool))
      .map(event => event.name))];
  };

  useEffect(() => {
    const filtered = (events || []).filter(event =>
      (selectedEventName ? event.name === selectedEventName : true) &&
      ((eventType === 'solo' && event.solo_bool) || (eventType === 'group' && !event.solo_bool))
    );
    setFilteredEvents(filtered);
  }, [selectedEventName, eventType, events]);

  const handleEventNameChange = (value) => {
    setSelectedEventName(value);
  };

  const handleEventTypeChange = (e) => {
    setEventType(e.target.value);
    setSelectedEventName(''); // Clear selected event name when event type changes  
  };

  const fetchAndShowApplications = (event) => {
    setCurrentEvent(event);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Event Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.length - b.name.length,
      sortDirections: ['descend'],
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            showSearch
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            placeholder="Select an event"
            optionFilterProp="children"
            onChange={value => {
              setSelectedKeys(value ? [value] : []);
              handleEventNameChange(value);
              confirm();
            }}
            value={selectedEventName}
          >
            {getUniqueEventNames().map(name => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
          <Button
            onClick={() => {
              clearFilters();
              handleEventNameChange('');
              confirm();
            }}
            size="small"
            style={{ width: 90, marginTop: 8 }}
          >
            Clear
          </Button>
        </div>
      ),
      filterIcon: (filtered) => <DeleteOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.name.includes(value),
    }
  ];

  const expandedRowRender = (record) => {
    const eventDetails = filteredEvents.filter(event => event.name === record.name);
    const innerColumns = [
      {
        title: 'Style',
        dataIndex: 'style',
        key: 'style'
      },
      {
        title: 'Group Type',
        dataIndex: 'solo_bool',
        key: 'solo_bool',
        render: soloBool => soloBool ? 'Solo' : 'Group'
      },
      {
        title: 'Venue',
        dataIndex: ['venue', 'venue'], // Access nested venue object and its venue field  
        key: 'venue'
      },
      {
        title: 'Duration (seconds)',
        dataIndex: 'duration',
        key: 'duration'
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (text, record) => (
          <Space>
            <Button onClick={() => fetchAndShowApplications(record)}>View Applications</Button>
            {userRole !== 'admin' ? (
              <Button onClick={() => openPopup(record)}>Apply</Button>
            ) : (
              <>
                <Button onClick={() => handleEditEvent(record)} icon={<EditOutlined />}>Edit</Button>
                <Popconfirm
                  title="Are you sure you want to delete this event?"
                  onConfirm={() => handleDeleteEvent(record._id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Delete Event"><Button danger icon={<DeleteOutlined />} shape='circle' type='text' /></Tooltip>
                </Popconfirm>
              </>
            )}
          </Space>
        )
      }
    ];
    return <Table columns={innerColumns} dataSource={eventDetails} pagination={false} rowKey="_id" scroll={{ x: true }} />;
  };

  const uniqueEvents = getUniqueEventNames().map(name => ({ name }));

  return (
    <>
      <Space style={{ marginBottom: 16, justifyContent: 'flex-end', width: '100%' }}>
        <Radio.Group onChange={handleEventTypeChange} value={eventType}>
          <Radio.Button value="solo">Solo</Radio.Button>
          <Radio.Button value="group">Group</Radio.Button>
        </Radio.Group>
      </Space>
      {loading ? (
        <Skeleton active />
      ) : (
        <Table
          loading={loading}
          dataSource={uniqueEvents}
          columns={columns}
          rowKey="name"
          expandable={{
            expandedRowRender,
          }}
          scroll={{ x: true }}
        />
      )}
      {currentEvent && (
        <EventApplicationsModal
          event={currentEvent}
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
        />
      )}
    </>
  );
};

export default EventTable;  